import download from 'downloadjs';
import { ICaseTemplate, IFilterDTO, ITemplate, ITemplateCriteria, IApproveTemplateCriteria } from '../../types/types';
import { createFilterCriteria } from '../../utilities/filterCriteria';
import { getStatusIdsFromText } from '../../utilities/templateStatus';
import { AlertType } from '../../types/enums';

export const downloadMailingFileTemplate = async () => {
	

	let response = await fetch(`api/document/downloadMailingFileTemplate/`, {
		method: 'GET',
		headers: {
			"Content-Type": "application/octet-stream"			
		}
	});

	if (!response.ok)
		throw new Error(response.statusText);

	let blob = await response.blob();
	let file = new Blob([blob], { type: 'application/octet-stream' });
	let fileName = response.headers.get('Content-Disposition')?.split(';')[1].trim().split('=')[1].replaceAll('"', '');
	download(file, fileName, "application/octet-stream");
};

export const downloadTemplate = async (templateId: string) => {
	let response = await fetch(`api/document/claimtemplate/${templateId}`, {
		method: 'GET',
		headers: {
			"Content-Type": "application/octet-stream"
		}
	});

	if (!response.ok)
		throw new Error(response.statusText);

	let blob = await response.blob();
	let file = new Blob([blob], { type: 'application/octet-stream' });
	let fileName = response.headers.get('Content-Disposition')?.split(';')[1].trim().split('=')[1].replaceAll('"', '');
	download(file, fileName, "application/octet-stream");
};


export const getTemplatesAsync = async (filter: IFilterDTO[]): Promise<ITemplate[]> => {
	let criteria = createFilterCriteria(filter) as ITemplateCriteria;

	if (criteria.statusText) {
		criteria.statusList = getStatusIdsFromText(criteria.statusText);
		delete criteria.statusText;
	}

	let templates: ITemplate[] = [] as ITemplate[];

	try {
		let response = await fetch(`api/Template/List`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json'				
			},
			body: JSON.stringify(criteria)
		});

		if (!response.ok) {
			throw new Error("Failed to get templates.");
		}

		templates = await response.json();
	}

	catch (exception) {
		console.error(exception)
		throw exception;
	}

	return templates;
};

export const getTemplateDetailAsync = async (templateId: number): Promise<ITemplate> => {
	
	let template: ITemplate = {} as ITemplate;

	try {
		let response = await fetch(`api/Template/${templateId}`, {
			method: 'GET',
			headers: {
				'Content-Type': 'application/json'				
			}
		});

		if (!response.ok) {

			throw new Error("Failed to get template detail.");

		}

		template = await response.json();
	}

	catch (exception) {

		console.error(exception);
		throw exception;

	}

	return template;
};

/**
 * Completion check for use with useTemplateGenerationPolling when polling a standalone template's
 * regeneration status (as opposed to a case-template combo). The backend clears Template.DocumentId
 * before queueing regeneration, so a non-null documentId indicates the regeneration has finished.
 */
export const checkTemplateGenerationCompleteAsync = async (templateId: number): Promise<boolean> => {
	const template = await getTemplateDetailAsync(templateId);
	return !!(template && template.documentId);
};

/**
 * Triggers regeneration of a standalone template (via the Template List's "Generate" action) and
 * waits for the HTTP response confirming the regeneration request was successfully queued with the
 * Template Generator service. This does not wait for generation to actually finish - callers should
 * follow up with checkTemplateGenerationCompleteAsync (e.g. via useTemplateGenerationPolling) to await
 * completion, mirroring the saveTemplate/PostGenerationMessage pattern.
 */
export const generateTemplateAsync = async (templateId: number) => {
	const generateTemplateResponse = { severity: AlertType.success, message: 'Template generation started.', result: false };

	try {
		let response = await fetch(`api/Template/GenerateTemplate/${templateId}`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json'
			}
		});

		const result = await response.json();

		generateTemplateResponse.message = result?.message ?? (response.ok ? 'Template generation started.' : 'Failed to generate template');
		generateTemplateResponse.severity = result?.severity ?? (response.ok ? AlertType.info : AlertType.error);
		generateTemplateResponse.result = response.ok ? true : (result?.severity !== AlertType.error);

	} catch (exception) {
		console.error(exception)
		generateTemplateResponse.message = 'Failed to generate template.';
		generateTemplateResponse.severity = AlertType.error;
	}

	return generateTemplateResponse;
}

export const saveTemplate = async (templateDetails: ITemplate) => {
	
	const saveTemplateResponse = { severity: AlertType.success, message: 'Template saved successfully.', result: false }

	try {
		let response = await fetch(`api/Template/UpdateTemplate`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json'				
			},
			body: JSON.stringify(templateDetails)
		});

		const result = await response.json();

		saveTemplateResponse.message = result?.message ?? (response.ok? 'Template updated successfully' : 'Failed to update template');
		saveTemplateResponse.severity = result?.severity ?? (response.ok ? AlertType.info : AlertType.error);
		saveTemplateResponse.result = response.ok ? true : (result?.severity != AlertType.error);

	} catch (exception) {
		console.error(exception)
		saveTemplateResponse.message = 'Failed to update template.';
		saveTemplateResponse.severity = AlertType.error;
	}

	return saveTemplateResponse;
}

export const getCaseTemplateAsync = async (caseId: number): Promise<ICaseTemplate> => {

	let caseTemplate: ICaseTemplate = {} as ICaseTemplate;

	try {
		let response = await fetch(`api/Template/GetCaseTemplate/${caseId}`, {
			method: 'GET',
			headers: {
				'Content-Type': 'application/json',
			},
		});

		if (!response.ok) {
			throw new Error("Failed to get templates.");
		}

		caseTemplate = await response.json();
	}

	catch (exception) {
		console.error(exception)
		throw exception;
	}

	return caseTemplate;
};


export const saveCaseTemplate = async (caseTemplate: ICaseTemplate) => {
	let result = false;

	try {
			let response = await fetch(`api/Template/SaveCaseTemplate`, {
			method: 'Post',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(caseTemplate)
		});

		if (!response.ok) {
			throw new Error("Failed to add case template...");
		}

		result = await response.json();
	}

	catch (exception) {

		console.error(exception)
		throw exception;

	}

	return result;

}

export const approveTemplate = async (criteria: IApproveTemplateCriteria) => {


	try {
		let response = await fetch(`api/Template/ApproveTemplate/`, {
			method: "POST",
			headers: {
				"Content-Type": "application/json"
			},
			body: JSON.stringify(criteria),
		});

		if (!response.ok) {
			throw new Error("Failed to approve template.");
		}

		return response.json();
	}
	catch (exception) {
		console.error(exception)
		throw exception;
	}
};

export const denyTemplate = async (criteria: IApproveTemplateCriteria) => {
	try {
		let response = await fetch(`api/Template/DenyTemplate/`, {
			method: "POST",
			headers: {
				"Content-Type": "application/json"
			},
			body: JSON.stringify(criteria),
		});

		if (!response.ok) {

			throw new Error("Failed to deny template.");
		}

		return response.json();
	}
	catch (exception) {
		console.error(exception)
		throw exception;
	}
};

export const deleteTemplate = async (id: number) => {
	try {
		let response = await fetch(`api/Template/DeleteTemplate/${id}`, {
			method: "GET",
			headers: {
				"Content-Type": "application/json"
			}
		});

		if (!response.ok) {

			throw new Error("Failed to delete template.");
		}

		return response.json();
	}
	catch (exception) {
		console.error(exception)
		throw exception;
	}
};